/**
 * Sidebar panel for paying an invoice.
 *
 * Fetches payment options (supported types + saved methods) on open,
 * lets the user pick a saved payment method and amount (partial payments
 * allowed), then submits for immediate capture via the portal API.
 *
 * PD-16381 — one "Payment Method" dropdown of every saved method the invoice
 * can be paid with, each priced in its label, replaces the old "Payment Type"
 * radio list followed by a per-type method picker. The type is a property of
 * the method chosen, so asking for it first was a step that could only
 * disagree with the choice that followed.
 */

import { faPlus, faWallet } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	calculatePaymentMethodFee,
	filterUsablePaymentMethods,
	formatPaymentMethodLabel,
	formatPaymentMethodOptionLabel,
	paymentMethodsEligibleForDefault,
	resolveAddPaymentMethodType,
	resolvePaymentMethodFeeRate,
} from '@supply-mover-2-0/core';
import type {
	PaymentDecline,
	PaymentRestrictionModel,
	PortalCheckoutPaymentMethodDto,
	SupportedPaymentMethodType,
} from '@supply-mover-2-0/shared';
import {
	ACCOUNT_FUNDS_METHOD_TYPE,
	formatPaymentRestrictionMessage,
	isPartialAccountFundsSpend,
	NO_PAYMENT_RESTRICTIONS,
} from '@supply-mover-2-0/shared';
import {
	CardBrandIcon,
	extractPaymentDecline,
	PaymentDeclineModal,
	Select,
	Toast,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { invalidatePaymentAvailability } from '../hooks/useCanMakePayment';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { toCustomerDecline } from '../lib/customerDecline';
import { formatCurrency } from '../lib/formatCurrency';
import { triggerLightImpact } from '../lib/haptics';
import * as portalInvoiceService from '../services/portalInvoiceService';
import { CurrencyInput } from './CurrencyInput';
import { PaymentSummary } from './PaymentSummary';
import { PortalAddPaymentMethod } from './PortalAddPaymentMethod';
import { PortalSidebar } from './PortalSidebar';

/**
 * PD-15878 — the saved methods in the order they should be defaulted to for
 * THIS invoice: the invoice's own account's primary, then its other methods,
 * then other accounts' primaries, then the rest. Stable within each band, so
 * the server's newest-first order still breaks ties.
 *
 * The list can span several accounts — a user assigned to more than one sees
 * every account's methods on one invoice — and each account carries its own
 * primary. The server orders the list newest-primary-first across accounts,
 * so "the first primary" was a sibling account's card whenever that card was
 * the more recently saved one, and the customer's own designated default was
 * never offered.
 */
const rankForInvoice = (
	methods: Array<PortalCheckoutPaymentMethodDto>,
	invoiceClientId: number | null,
): Array<PortalCheckoutPaymentMethodDto> => {
	const band = (m: PortalCheckoutPaymentMethodDto): number => {
		const own = m.ownerClientId === invoiceClientId;
		if (own && m.isPrimary) {
			return 0;
		}
		if (own) {
			return 1;
		}
		return m.isPrimary ? 2 : 3;
	};
	return methods
		.map((m, index) => ({ m, index, band: band(m) }))
		.sort((a, b) => (a.band !== b.band ? a.band - b.band : a.index - b.index))
		.map(({ m }) => m);
};

interface PayInvoiceSidebarProps {
	isOpen: boolean;
	onClose: () => void;
	invoiceId: number;
	outstandingBalance: number;
	invoiceExternalId: string | null;
	/**
	 * PD-15688 — the processing fee already embedded in this invoice's
	 * balance. When it is positive the balance is fee-inclusive and the
	 * server charges it as-is (PD-15473), so quoting another fee here would
	 * both misstate the charge and read as a fee on a fee.
	 */
	carriedProcessingFee?: number;
	onPaymentSuccess: (updatedBalance: string) => void;
}

export const PayInvoiceSidebar: React.FC<PayInvoiceSidebarProps> = ({
	isOpen,
	onClose,
	invoiceId,
	outstandingBalance,
	invoiceExternalId,
	carriedProcessingFee = 0,
	onPaymentSuccess,
}) => {
	const { t } = useTranslation();
	const { token } = usePortalAuth();
	const queryClient = useQueryClient();

	// Payment options from server
	const [paymentTypes, setPaymentTypes] = useState<
		Array<SupportedPaymentMethodType>
	>([]);
	const [savedMethods, setSavedMethods] = useState<
		Array<PortalCheckoutPaymentMethodDto>
	>([]);
	// PD-15878 — the account the invoice belongs to. The saved methods span
	// every account the user can pay from, so "the primary" is ambiguous
	// without it.
	const [invoiceClientId, setInvoiceClientId] = useState<number | null>(null);
	// PD-16733 — the invoice's account by name (the empty state names it), and
	// the account a method added from this panel is saved to (the add form
	// names it). The server picks the latter from the invoice's payer
	// accounts, so a method added here is listed here.
	const [invoiceClientName, setInvoiceClientName] = useState<string | null>(
		null,
	);
	const [newMethodClientName, setNewMethodClientName] = useState<
		string | null
	>(null);
	// PD-17009 — that account's id, so the add form can tell whether it
	// belongs to an ownership group and may offer the share switch.
	const [newMethodClientId, setNewMethodClientId] = useState<number | null>(
		null,
	);
	// PD-15295 — the invoice client's account-funds position; drives the
	// Account Funds payment type row and caps the amount.
	const [accountFunds, setAccountFunds] = useState<{
		enabled: boolean;
		available: number;
	} | null>(null);
	// PD-15585 — the tenant's rule that Account Funds must be spent in full,
	// with the portal wording. Nothing blocked until the options say so.
	const [partialFundsRestriction, setPartialFundsRestriction] =
		useState<PaymentRestrictionModel>(
			NO_PAYMENT_RESTRICTIONS.partialAccountFundsSpend,
		);
	const [optionsLoading, setOptionsLoading] = useState(false);

	// Form state
	const [amount, setAmount] = useState('');
	/**
	 * PD-15585 — "use my account funds" for THIS payment, the same split
	 * tender the cart offers (PD-15084). Never remembered and never on by
	 * default: the balance is only spent when the customer ticks the box
	 * beside the figure it would spend.
	 */
	const [useAccountFunds, setUseAccountFunds] = useState(false);
	const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// PD-15550 — a gateway refusal is a dialog, not a line of inline text.
	// Held separately from `error` so the two never render at once.
	const [decline, setDecline] = useState<PaymentDecline | null>(null);

	// Add payment method sidebar (nested)
	const [showAddPayment, setShowAddPayment] = useState(false);

	/** Saved-method ids seen on the last fetch of THIS open session — a
	 *  refetch that surfaces a new id means the user just added that method
	 *  through the nested sidebar (PD-15014). */
	const knownMethodIdsRef = useRef<Set<number> | null>(null);

	// Fetch payment options when sidebar opens
	const fetchPaymentOptions = useCallback(async () => {
		if (token == null) {
			return;
		}

		setOptionsLoading(true);
		try {
			const res = await portalInvoiceService.getInvoicePaymentOptions(
				token,
				invoiceId,
			);
			if (res.success) {
				const types = res.data.paymentTypes;
				const methods = res.data.savedPaymentMethods;
				setPaymentTypes(types);
				setSavedMethods(methods);
				setInvoiceClientId(res.data.invoiceClientId);
				setInvoiceClientName(res.data.invoiceClientName ?? null);
				setNewMethodClientName(res.data.newMethodClientName ?? null);
				setNewMethodClientId(res.data.newMethodClientId ?? null);
				setAccountFunds(res.data.accountFunds ?? null);
				setPartialFundsRestriction(
					res.data.paymentRestrictions?.partialAccountFundsSpend ??
						NO_PAYMENT_RESTRICTIONS.partialAccountFundsSpend,
				);

				// PD-15585 — the gateway types this invoice can be paid with.
				// Account Funds is the checkbox, not a method, so it is left out.
				const gatewayTypes = types.filter(
					(pt) => pt.paymentMethodType !== ACCOUNT_FUNDS_METHOD_TYPE,
				);
				const usable = filterUsablePaymentMethods(methods, gatewayTypes);
				const prevKnown = knownMethodIdsRef.current;
				knownMethodIdsRef.current = new Set(methods.map((m) => m.id));
				const fresh =
					prevKnown == null ? null : usable.find((m) => !prevKnown.has(m.id));
				// PD-15014 — a method added mid-flow is the one the user means to
				// pay with, so it is selected. Otherwise the invoice's own
				// account's primary leads (PD-15878, see `rankForInvoice`).
				//
				// PD-17009 — a method another account shares is never the
				// default: it is offered, and the customer chooses it or not.
				setSelectedMethodId(
					fresh?.id ??
						rankForInvoice(
							paymentMethodsEligibleForDefault(usable),
							res.data.invoiceClientId,
						).at(0)?.id ??
						null,
				);
			}
		} catch (err) {
			console.error('Failed to fetch payment options:', err);
			// PD-15585 — the server's own reason, when it gave one. A blanket
			// "Failed to load payment options" told a customer looking at a
			// document the list had just linked them to precisely nothing they
			// could act on.
			const reason = err instanceof Error ? err.message.trim() : '';
			setError(
				reason.length > 0 ? reason : t('Failed to load payment options'),
			);
		} finally {
			setOptionsLoading(false);
		}
	}, [token, invoiceId, t]);

	useEffect(() => {
		if (isOpen) {
			// Reset state when opening (a fresh baseline for the just-added
			// method detection too — ids from a previous open don't count).
			knownMethodIdsRef.current = null;
			setAmount(outstandingBalance.toFixed(2));
			setUseAccountFunds(false);
			setError(null);
			setDecline(null);
			setSubmitting(false);
			void fetchPaymentOptions();
		}
	}, [isOpen, outstandingBalance, fetchPaymentOptions]);

	// PD-15585 — Account Funds is no longer a payment TYPE the customer picks.
	// It is a checkbox above the method picker that spends the balance first
	// and leaves the remainder to the chosen method, so a balance smaller than
	// the bill can no longer be selected as if it could settle the whole thing.
	// The type row is still needed to SUBMIT a payment the funds cover
	// outright, which goes up as a plain Account Funds payment (as the cart
	// does), so it is kept out of the picker rather than out of the data.
	const accountFundsType =
		paymentTypes.find(
			(pt) => pt.paymentMethodType === ACCOUNT_FUNDS_METHOD_TYPE,
		) ?? null;
	const gatewayPaymentTypes = paymentTypes.filter(
		(pt) => pt.paymentMethodType !== ACCOUNT_FUNDS_METHOD_TYPE,
	);

	const fundsAvailable = accountFunds?.available ?? 0;
	const fundsOffered = (accountFunds?.enabled ?? false) && fundsAvailable > 0;

	// PD-16381 — every saved method this invoice can be paid with, cards and
	// bank accounts together, in the server's order (primary first, then
	// newest). A method whose type the invoice does not accept is left out.
	const usableMethods = filterUsablePaymentMethods(
		savedMethods,
		gatewayPaymentTypes,
	);

	// PD-15878 — the id actually in play, guaranteed to be one the picker is
	// showing. A <select> whose `value` matches no `<option>` displays its
	// first option without changing the value behind it, so a selection that
	// is no longer offered let the screen and the charge disagree. The fee
	// preview, the picker and the submit all read THIS, so the method
	// displayed is by construction the method charged.
	//
	// PD-17009 — the fallback passes over methods another account shares, so
	// a panel whose only options are shared ones opens with nothing chosen
	// (the picker shows its placeholder and Pay stays held) rather than with
	// someone else's card already selected.
	const effectiveMethodId =
		usableMethods.find((m) => m.id === selectedMethodId)?.id ??
		rankForInvoice(
			paymentMethodsEligibleForDefault(usableMethods),
			invoiceClientId,
		).at(0)?.id ??
		null;

	// A method stored on another account (the parent's card on a child's
	// invoice, or the signed-in account's card on an ownership-group member's
	// invoice) is labelled with its owner even when it is the only owner on
	// offer: the customer is choosing to pay this account's bill with a card
	// that lives elsewhere, and the label is what tells them so. Methods on the
	// invoice's own account stay unlabelled unless the list spans owners.
	const showsForeignOwner = usableMethods.some(
		(m) => invoiceClientId != null && m.ownerClientId !== invoiceClientId,
	);

	// Methods bucketed by the account they are stored on, preserving the
	// server's ordering (primary first, then newest) within each bucket.
	const methodOwnerGroups = usableMethods.reduce<
		Array<{
			ownerClientId: number;
			ownerClientName: string | null;
			methods: Array<PortalCheckoutPaymentMethodDto>;
		}>
	>((groups, m) => {
		const existing = groups.find((g) => g.ownerClientId === m.ownerClientId);
		if (existing != null) {
			existing.methods.push(m);
			return groups;
		}
		groups.push({
			ownerClientId: m.ownerClientId,
			ownerClientName: m.ownerClientName,
			methods: [m],
		});
		return groups;
	}, []);

	// Processing fee preview
	const parsedAmount = parseFloat(amount);
	const isValidAmount =
		!isNaN(parsedAmount) &&
		parsedAmount > 0 &&
		parsedAmount <= outstandingBalance;
	// PD-15585 — what the balance would take off this payment, and whether it
	// settles it outright (in which case no method is needed at all).
	const fundsApplied =
		useAccountFunds && isValidAmount
			? Math.round(Math.min(fundsAvailable, parsedAmount) * 100) / 100
			: 0;
	const fundsCoverEverything =
		useAccountFunds && isValidAmount && fundsAvailable >= parsedAmount - 0.005;
	// PD-15585 — a balance LARGER than this payment would be spent only in
	// part, which the tenant's ERP may be unable to apply. Named under the
	// checkbox before Pay is pressed; the server refuses it the same way.
	// A balance the payment can absorb is consumed whole and is unaffected.
	const partialFundsBlocked =
		partialFundsRestriction.blocked &&
		useAccountFunds &&
		isValidAmount &&
		isPartialAccountFundsSpend(parsedAmount, fundsAvailable);
	const partialFundsMessage = formatPaymentRestrictionMessage(
		partialFundsRestriction.message,
		formatCurrency(fundsAvailable) ?? '',
	);
	// PD-15173 / PD-16381 — the fee is the selected METHOD's: its type's
	// server-quoted rate, and only when that method may be surcharged (a debit
	// or prepaid card never is). Resolved through the same helper that prices
	// each option in the picker, so the label and the charge preview agree.
	const selectedMethod = usableMethods.find((m) => m.id === effectiveMethodId);
	const feeRate =
		selectedMethod != null
			? resolvePaymentMethodFeeRate(gatewayPaymentTypes, selectedMethod)
			: null;
	// PD-15688 — an invoice whose balance already embeds a processing fee is
	// charged that balance and nothing more, so no fee is quoted for it. This
	// is the same fee-bearing/fee-free split the CRM Make Payment screen has
	// always made; the portal could not make it until the carried figure was
	// carried through to this surface.
	const carriesFee = carriedProcessingFee > 0.005;
	// PD-15084/PD-15585 — a fee follows the form of payment, and Account Funds
	// carries none (PD-15082), so the rate falls only on the share the method
	// actually collects. A payment the funds cover outright is charged nothing
	// and so carries no fee at all.
	const feeBase = Math.round((parsedAmount - fundsApplied) * 100) / 100;
	const feeAmount =
		feeRate != null && isValidAmount && !carriesFee && feeBase > 0.005
			? calculatePaymentMethodFee(feeBase, feeRate)
			: 0;
	const totalWithFee = isValidAmount ? parsedAmount + feeAmount : 0;

	/**
	 * PD-16381 — each option names the method on its first line and, smaller
	 * on the second, what paying with it costs (PD-15011: "No fee" said
	 * outright) and, where the list spans accounts, whose it is. An invoice
	 * whose balance already carries a fee is charged as-is (PD-15688), so no
	 * option advertises a rate there; the summary line says the fee is already
	 * included.
	 */
	const methodOption = (
		m: PortalCheckoutPaymentMethodDto,
	): ReturnType<typeof formatPaymentMethodOptionLabel> =>
		formatPaymentMethodOptionLabel(m, t, {
			feeTypes: carriesFee ? null : gatewayPaymentTypes,
			accountName:
				methodOwnerGroups.length > 1 || showsForeignOwner
					? m.ownerClientName
					: null,
		});

	const canSubmit =
		isValidAmount &&
		!partialFundsBlocked &&
		// A payment the balance settles outright needs no method; anything the
		// method still has to collect needs one.
		(fundsCoverEverything || effectiveMethodId != null) &&
		!submitting;

	/**
	 * PD-15585 — why Pay is greyed out, said beside the button. The reasons
	 * above are each stated where they arise; a customer who has scrolled to
	 * the button should not have to hunt for the one that is stopping them.
	 */
	const blockingReason: string | null = !isValidAmount
		? isNaN(parsedAmount) || parsedAmount <= 0
			? t('Enter an amount to pay.')
			: t('Cannot exceed outstanding balance')
		: partialFundsBlocked
			? t(partialFundsMessage)
			: !fundsCoverEverything && effectiveMethodId == null
				? t('Choose a payment method to collect the remainder.')
				: null;

	const handleSubmit = async (): Promise<void> => {
		if (token == null || !canSubmit) {
			return;
		}

		setSubmitting(true);
		setError(null);
		setDecline(null);

		try {
			// PD-15585 — full cover submits as a plain Account Funds payment;
			// a balance that only covers part rides the applyAccountFunds flag
			// alongside the chosen method, which collects the remainder. Same
			// shape the cart submits (PD-15084).
			const res = await portalInvoiceService.payInvoice(
				token,
				invoiceId,
				fundsCoverEverything
					? {
							paymentAmount: parsedAmount,
							paymentMethodType: ACCOUNT_FUNDS_METHOD_TYPE,
						}
					: {
							paymentAmount: parsedAmount,
							paymentMethodId: effectiveMethodId ?? undefined,
							...(useAccountFunds ? { applyAccountFunds: true } : {}),
						},
			);

			if (res.success) {
				// PD-14695: a light tap confirms the payment landed.
				triggerLightImpact();

				// PD-15550 — success closes this sidebar, so without a toast the
				// customer is returned to the invoice with nothing on screen
				// saying the payment went through. State the amount actually
				// taken (a surcharged card is charged the total, not the payment
				// figure they typed) and what the invoice owes afterwards, so the
				// confirmation answers both questions the receipt would.
				const chargedAmount = feeAmount > 0 ? totalWithFee : parsedAmount;
				const invoiceRef = invoiceExternalId ?? String(invoiceId);
				const paidText = t(
					`${formatCurrency(chargedAmount) ?? ''} paid on invoice #${invoiceRef}`,
				);
				// The server returns the new balance as a string; if it ever fails
				// to parse, say nothing about the remainder rather than "NaN".
				const remainingBalance = parseFloat(res.updatedOutstandingBalance);
				const remainingText = Number.isNaN(remainingBalance)
					? null
					: remainingBalance <= 0
						? t('Invoice paid in full')
						: t(`Remaining balance: ${formatCurrency(remainingBalance) ?? ''}`);

				Toast.show({
					type: 'success',
					text1: t('Payment submitted'),
					text2:
						remainingText == null
							? paidText
							: `${paidText} ${t('·')} ${remainingText}`,
					duration: 5000,
				});

				// PD-16789 — paying this invoice may leave nothing to pay.
				void invalidatePaymentAvailability(queryClient);
				onPaymentSuccess(res.updatedOutstandingBalance);
				onClose();
			}
		} catch (err) {
			// PD-15550 — a refusal from the gateway is presented as a dialog the
			// customer has to acknowledge, matching every other portal payment
			// surface (PD-15185). The sidebar stays open behind it so they can
			// pick a different method without reopening the flow. Anything that
			// is not a decline (a network drop, a validation error) is still the
			// inline message: dressing those as a decline would misreport them.
			const paymentDecline = toCustomerDecline(extractPaymentDecline(err));

			if (paymentDecline != null) {
				setDecline(paymentDecline);
			} else {
				const message =
					err instanceof Error
						? err.message
						: t('Payment failed. Please try again.');
				setError(message);
			}
		} finally {
			setSubmitting(false);
		}
	};

	// After adding a new payment method, refresh options and auto-select it
	const handleAddPaymentSuccess = (): void => {
		setShowAddPayment(false);
		void fetchPaymentOptions();
		// PD-16733 — the method may have been saved to the signed-in account,
		// whose Account page card lists its methods from this cache.
		void queryClient.invalidateQueries({
			queryKey: ['portal-payment-methods'],
		});
	};

	return (
		<>
			<PortalSidebar
				isOpen={isOpen}
				onClose={onClose}
				title={t('Pay Invoice')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					{/* Invoice reference */}
					<div className="mb-4 pb-4 border-b border-brand-border">
						<p className="text-sm text-brand-muted">
							{t('Invoice')} #{t(invoiceExternalId ?? String(invoiceId))}
						</p>
						<p className="text-lg font-bold text-default">
							{t('Outstanding')}: {t(formatCurrency(outstandingBalance) ?? '')}
						</p>
					</div>

					{optionsLoading && (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-portal-accent" />
						</div>
					)}

					{!optionsLoading && (
						<>
							{/* Amount */}
							<div className="mb-4">
								<label className="block text-sm font-medium text-strong mb-1">
									{t('Payment Amount')}
								</label>
								{/* PD-15974 — the CRM's cents-shift currency mask; see CurrencyInput. */}
								<CurrencyInput
									value={amount}
									onChange={(next) => {
										setAmount(next);
										setError(null);
									}}
									size="lg"
								/>
								{!isNaN(parsedAmount) && parsedAmount > outstandingBalance && (
									<p className="text-xs text-danger mt-1">
										{t('Cannot exceed outstanding balance')}
									</p>
								)}
							</div>

							{/* PD-15585 — the account-funds opt-in. Above the payment
							    method because it decides how much of the payment they are
							    being asked to collect — and, when the balance covers the
							    whole thing, whether a method is needed at all. */}
							{fundsOffered && accountFundsType != null && (
								<div className="mb-4 pb-4 border-b border-brand-border">
									<label className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input">
										<input
											type="checkbox"
											checked={useAccountFunds}
											onChange={(e) => {
												setUseAccountFunds(e.target.checked);
												setError(null);
											}}
											className="w-4 h-4 text-portal-accent"
										/>
										<FontAwesomeIcon icon={faWallet} className="text-default" />
										<span className="text-sm text-default">
											{t('Use my account funds')}
										</span>
										<span className="text-xs text-brand-muted ml-auto">
											{t(formatCurrency(fundsAvailable) ?? '')} {t('available')}
										</span>
									</label>
									{useAccountFunds && partialFundsBlocked && (
										<p className="ml-9 text-xs text-danger mt-1">
											{t(partialFundsMessage)}
										</p>
									)}
									{useAccountFunds && !partialFundsBlocked && isValidAmount && (
										<p className="ml-9 text-xs text-brand-muted mt-1">
											{fundsCoverEverything
												? t(
														'Your account funds cover this payment — no other payment method is needed.',
													)
												: `${t('Applies')} ${formatCurrency(fundsApplied) ?? ''} ${t('now; the remaining')} ${formatCurrency(Math.round((totalWithFee - fundsApplied) * 100) / 100) ?? ''} ${t('is collected from the payment method selected below.')}`}
										</p>
									)}
								</div>
							)}

							{/* PD-16381 — one picker of every saved method the invoice can be
							    paid with, each priced in its label. Not applicable when Account
							    Funds settle the payment, since nothing is left to collect. */}
							{gatewayPaymentTypes.length > 0 && !fundsCoverEverything && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-strong mb-1">
										{t('Payment Method')}
									</label>
									{usableMethods.length > 0 ? (
										// PD-15878 — the shared Select, so each option carries its network's
										// mark: a card is recognized by its logo well before its four digits.
										// Where more than one account's methods are offered, the owning account
										// rides on the option's second line — two cards can end in the same
										// four digits. PD-16381 — the closed control keeps the fee beside the
										// method's name.
										<Select
											aria-label={t('Payment Method')}
											placeholder={t('Choose a payment method')}
											value={effectiveMethodId ?? ''}
											onChange={(next) => {
												setSelectedMethodId(
													next === '' ? null : parseInt(next, 10),
												);
											}}
											options={usableMethods.map((m) => {
												const option = methodOption(m);
												return {
													value: m.id,
													label: option.primary,
													subtitle: option.secondary,
													selectedSuffix: option.fee,
													icon: (
														<CardBrandIcon
															brand={m.subtype ?? m.type}
															className="text-xl"
															title={formatPaymentMethodLabel(m, t)}
														/>
													),
												};
											})}
										/>
									) : (
										<p className="text-xs text-brand-muted py-1">
											{/* PD-16733 — which account's methods are missing, since
											    the signed-in account's own cards are not always
											    offered on another account's invoice (PD-15975). */}
											{invoiceClientName != null && invoiceClientName !== ''
												? t('No saved payment methods for {{account}} yet.', {
														account: t(invoiceClientName),
													})
												: t('No saved payment methods yet.')}
										</p>
									)}
									<button
										type="button"
										onClick={() => setShowAddPayment(true)}
										className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:underline mt-2"
									>
										<FontAwesomeIcon icon={faPlus} />
										<span>{t('Add new payment method')}</span>
									</button>
								</div>
							)}

							{/* PD-15688 — the checkout summary. Always rendered once the
							    amount is valid (it used to appear only when a fee was
							    due, so a fee-free payment showed no total at all), and
							    the Pay button below repeats its grand total. */}
							{isValidAmount && (
								<div className="mb-4 p-3 bg-brand-input rounded-lg">
									<PaymentSummary
										total={
											Math.round((totalWithFee - fundsApplied) * 100) / 100
										}
										lines={[
											{ label: 'Payment', amount: parsedAmount },
											// PD-15585 — what the balance takes off the charge,
											// deducted as its own line so the card's share is
											// visible rather than inferred.
											{
												label: 'Account funds applied',
												amount: -fundsApplied,
												hideWhenZero: true,
											},
											{
												label: 'Processing fee',
												amount: feeAmount,
												hideWhenZero: true,
											},
											...(carriesFee
												? [
														{
															label: 'Processing fee',
															amount: 0,
															hint: 'Already included in this invoice balance.',
														},
													]
												: []),
										]}
									/>
								</div>
							)}

							{/* Error */}
							{error != null && (
								<div className="mb-4 p-3 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
									<p className="text-sm text-danger">{t(error)}</p>
								</div>
							)}

							{/* PD-15585 — why the button below is inert. */}
							{blockingReason != null && error == null && (
								<p className="mb-2 text-sm text-danger">{blockingReason}</p>
							)}

							{/* Submit */}
							<button
								type="button"
								onClick={() => void handleSubmit()}
								disabled={!canSubmit}
								className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
							>
								{submitting ? (
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
								) : null}
								{/* PD-15688 — the button states what the card is actually
								    charged (fee included). It previously showed the
								    pre-fee amount while the summary directly above it
								    showed a larger Total charge. Interpolated rather
								    than built into the key, so one translation entry
								    covers every amount. */}
								{submitting
									? t('Processing...')
									: fundsCoverEverything
										? // PD-15585 — nothing is charged to a method, so
											// "Pay $0.00" misdescribes the button. The money is
											// real; it comes from the balance.
											t('Pay with account funds')
										: t('Pay {{amount}}', {
												amount:
													formatCurrency(
														isValidAmount
															? Math.round(
																	(totalWithFee - fundsApplied) * 100,
																) / 100
															: 0,
													) ?? '',
											})}
							</button>
						</>
					)}
				</div>
			</PortalSidebar>

			{/* Nested add payment method sidebar */}
			<PortalSidebar
				isOpen={showAddPayment}
				onClose={() => setShowAddPayment(false)}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={handleAddPaymentSuccess}
						onCancel={() => setShowAddPayment(false)}
						// PD-16733 — saved to an account that may pay THIS invoice,
						// so it appears in this panel once added.
						invoiceId={invoiceId}
						saveToAccountName={newMethodClientName}
						saveToClientId={newMethodClientId}
						supportedPaymentMethods={gatewayPaymentTypes}
						// PD-16381 — with no type step, the form opens on the
						// selected method's type (else a card), always one this
						// invoice accepts; its own selector switches the rest.
						preSelectedPaymentType={resolveAddPaymentMethodType(
							selectedMethod?.type,
							gatewayPaymentTypes,
						)}
					/>
				</div>
			</PortalSidebar>

			{/* PD-15550 — a refused payment, acknowledged deliberately. A sibling
			    of the Sidebars rather than a child, so it stacks above the open
			    panel and closing it leaves the panel exactly as it was. */}
			<PaymentDeclineModal
				decline={decline}
				onClose={(): void => {
					setDecline(null);
				}}
			/>
		</>
	);
};
